import { useState, useContext, useEffect } from 'react';
import { useQuery, useMutation, useApolloClient } from '@apollo/react-hooks';
import type { ApolloError } from 'apollo-client';

import { useSessionData } from '@confluence/session-data';
import { RoutesContext, getSingleParam } from '@confluence/route-manager';

import {
	HomeRecentUserPreferencesQuery,
	HomeRecentUserPreferencesMutation,
} from './HomeRecentQueries.graphql';
import type { HomeRecentUserPreferencesQuery as HomeRecentUserPreferencesQueryType } from './__types__/HomeRecentUserPreferencesQuery';
import { RecentFilter } from './__types__/HomeRecentUserPreferencesQuery';
import type {
	HomeRecentUserPreferencesMutation as HomeRecentUserPreferencesMutationType,
	HomeRecentUserPreferencesMutationVariables,
} from './__types__/HomeRecentUserPreferencesMutation';

type UseRecentPreferences = (defaultPreference?: RecentFilter) => {
	loading: boolean;
	queryError?: ApolloError;
	mutationError?: ApolloError;
	filter: RecentFilter;
	updateFilter: (filter: RecentFilter) => void;
};

type UseRecentPreferencesOverride = () => {
	filterOverride?: RecentFilter;
	clearFilterOverride: () => void;
};

export const useRecentPreferences: UseRecentPreferences = (
	defaultPreference = RecentFilter.ALL,
) => {
	const { isLoggedIn } = useSessionData();
	const apolloClient = useApolloClient();
	const {
		data,
		loading,
		error: queryError,
	} = useQuery<HomeRecentUserPreferencesQueryType>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		HomeRecentUserPreferencesQuery,
		{
			skip: !isLoggedIn,
		},
	);
	const filter = data?.userPreferences.recentFilter || defaultPreference;
	const [homeRecentUserPreferencesMutation, { error: mutationError }] = useMutation<
		HomeRecentUserPreferencesMutationType,
		HomeRecentUserPreferencesMutationVariables
	>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		HomeRecentUserPreferencesMutation,
	);

	const updateFilter = (updatedFilter: RecentFilter) => {
		if (isLoggedIn) {
			void homeRecentUserPreferencesMutation({
				variables: { filter: updatedFilter },
				optimisticResponse: {
					// @ts-ignore __typename does not exist on autogenerated graphql types
					__typename: 'Mutation',
					updateUserPreferences: {
						// @ts-ignore __typename does not exist on autogenerated graphql types
						__typename: 'UserPreferences',
						recentFilter: updatedFilter,
					},
				},
			}).catch(() => {}); // Ignore the error here since it is handled in useMutation

			// Always write to the cache so the user sees the preference even if there is an error
			apolloClient.writeQuery<HomeRecentUserPreferencesQueryType>({
				query: HomeRecentUserPreferencesQuery,
				data: {
					userPreferences: {
						// @ts-ignore __typename does not exist on autogenerated graphql types
						__typename: 'UserPreferences',
						recentFilter: updatedFilter,
					},
				},
			});
		}
	};

	return {
		loading,
		queryError,
		mutationError,
		filter,
		updateFilter,
	};
};

export const useAnonymousRecentPreference: UseRecentPreferences = (
	defaultPreference = RecentFilter.ALL,
) => {
	const [filter, setFilter] = useState(defaultPreference);
	return {
		loading: false,
		filter,
		updateFilter: setFilter,
	};
};

export const useRecentPreferencesOverride: UseRecentPreferencesOverride = () => {
	const { getQueryParams, setQueryParams, history } = useContext(RoutesContext);
	const filterQueryParam = getSingleParam(getQueryParams(), 'filter') as RecentFilter | undefined;
	const filter =
		filterQueryParam && Object.values(RecentFilter).includes(filterQueryParam)
			? filterQueryParam
			: undefined;
	const [filterOverride, setFilterOverride] = useState<RecentFilter | undefined>(filter);

	useEffect(() => {
		setQueryParams({ filter: undefined });
		return history?.listen(() => {
			const updatedFilterQueryParam = getSingleParam(getQueryParams(), 'filter') as
				| RecentFilter
				| undefined;
			if (updatedFilterQueryParam) {
				const updatedFilter = Object.values(RecentFilter).includes(updatedFilterQueryParam)
					? updatedFilterQueryParam
					: undefined;
				setFilterOverride(updatedFilter);
				setQueryParams({ filter: undefined });
			}
		});
	}, [setQueryParams, getQueryParams, history]);

	const clearFilterOverride = () => {
		setFilterOverride(undefined);
	};
	return {
		filterOverride,
		clearFilterOverride,
	};
};

export { RecentFilter } from './__types__/HomeRecentUserPreferencesQuery';
